/**
 * Private-charter airport doctrine for Inbox mint (Grok Fetch + Classic fallback).
 *
 * We do not fine-tune in the xAI console — this brief + Classic ranking is the
 * training. Dest order for a normal private charter:
 * 1. Prefer executive / GA / reliever when a sensible one exists.
 * 2. If no suitable executive field is on the pool / installed / usable,
 *    the commercial hub is the backup — never leave the RFQ without a dest.
 * 3. Hubs as a *primary* choice stay reserved for missed-connection /
 *    catch-airline stories (and mx positioning to the shop).
 *
 * Origins follow the live company bases (primary + extras / tail base).
 * A parked field is an origin only when it is one of those bases.
 * Destination mix follows those bases. CYPD is not a home unless it is one.
 */

export const COMMERCIAL_HUBS = [
  "CYYZ",
  "CYUL",
  "KBOS",
  "KJFK",
  "KEWR",
  "KMIA",
  "KFLL",
  "KMCO",
  "KCLT",
  "KIAD",
  "KPHL",
  "KBWI",
  "MMMX",
  "TJSJ",
] as const;

/** Executive / GA / reliever pads a midsize charter should prefer. */
export const EXECUTIVE_DESTS = [
  "KTEB",
  "KHPN",
  "KBED",
  "KMMU",
  "KFRG",
  "KORH",
  "KSWF",
  "KHEF",
  "CYHU",
  "CYKZ",
  "CYTZ",
  "CYHM",
  "CYKF",
  "CYOO",
  "KOPF",
  "KFXE",
  "KAPF",
  "KBCT",
  "KSDL",
  "MMTO",
] as const;

export type AirportFieldClass = "executive" | "hub" | "other";

const HUB_SET = new Set<string>(COMMERCIAL_HUBS);
const EXEC_SET = new Set<string>(EXECUTIVE_DESTS);

export function normalizeIcao(icao?: string | null): string {
  return (icao ?? "").trim().toUpperCase();
}

export function isCommercialHub(icao?: string | null): boolean {
  return HUB_SET.has(normalizeIcao(icao));
}

export function isExecutiveField(icao?: string | null): boolean {
  return EXEC_SET.has(normalizeIcao(icao));
}

export function airportFieldClass(icao?: string | null): AirportFieldClass {
  const code = normalizeIcao(icao);
  if (EXEC_SET.has(code)) return "executive";
  if (HUB_SET.has(code)) return "hub";
  return "other";
}

/** True when a hub is the intended *primary* dest (not merely a last-resort backup). */
export function hubsArePrimaryFor(purpose?: string | null): boolean {
  const id = (purpose ?? "").trim().toLowerCase();
  return id === "connection" || id === "mx_payload";
}

/**
 * Classic dest rank. Lower wins.
 * 1. Executive / GA / reliever first on normal RFQs.
 * 2. Other allow-list fields next; hubs last as backup so a legal pair always exists.
 * 3. Connection-miss / mx-positioning: hubs are first-class (primary).
 */
export function destPreferenceRank(
  icao: string,
  opts: {
    purpose?: string;
    prefer?: ReadonlySet<string>;
  } = {},
): number {
  const code = normalizeIcao(icao);
  const hubPrimary = hubsArePrimaryFor(opts.purpose);
  if (hubPrimary && isCommercialHub(code)) return 0;
  if (!hubPrimary && isExecutiveField(code)) return 0;
  if (opts.prefer?.has(code) && !isCommercialHub(code)) return 1;
  if (opts.prefer?.has(code)) return hubPrimary ? 1 : 3;
  if (!hubPrimary && isCommercialHub(code)) return 4;
  return 2;
}

function briefBases(bases: readonly string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of bases) {
    const icao = normalizeIcao(raw);
    if (icao.length < 3 || seen.has(icao)) continue;
    seen.add(icao);
    out.push(icao);
  }
  return out;
}

/** Airport brief for one mint. Empty bases must not fall back to Port Hawkesbury. */
export function formatGrokInboxAirportBrief(bases: readonly string[] = []): string {
  const live = briefBases(bases);
  const baseLine = live.length
    ? `Current company bases (live desk, primary first): ${live.join(", ")}. Most RFQs leave from one of these, or from the suggested tail's assigned base when that base is on the list. A parked field is an origin only when it is one of those bases.`
    : "No live company base was provided. Do not assume Port Hawkesbury, CYPD, Celtic Air, or an Atlantic seasonal pad.";
  return [
    "Airport mix for on-demand private charter (midsize jet / turboprop). Home field is whatever the desk snapshot lists — not a frozen CYPD / Port Hawkesbury career.",
    baseLine,
    "Priority for a normal private-charter dest: (1) prefer executive / GA / reliever when a sensible one exists for that trip (examples: KTEB, KHPN, KBED, KOPF, KFXE, KAPF, and the executive field nearest the current bases). (2) If no suitable executive field is on the allow-list, installed, or usable (range / jet stage / not the origin), use the commercial hub as the backup — never leave the RFQ without a destination. (3) Big hubs (CYYZ, CYUL, KBOS, KJFK, KEWR, KMIA, and similar) as a *primary* choice stay reserved for missed-connection / catch-airline stories — set seasonalPurpose=connection — and for mx positioning to the shop. Not the everyday default when an executive field exists.",
    "DO: Customer originIcao is a current company base or the tail's assigned base. Do not invent an origin at CYPD / Port Hawkesbury unless that ICAO is a current base. Do not default origin to the aircraft's last landing, parked field, or nearest-to-position airport unless that field is a current base — position is for assignment and ferries, not inbox RFQs.",
    "DO: Most dests are realistic charter demand within range of the current bases. Canada, the US, Caribbean, and Mexico are all legal when they are on the allow-list and the stage fits. Do not ban Canada. Do not force US-only. Soft executive preference stays. Do not collapse to KSWF / KPBI / KJFK / MYNN / KBCT / CYPD loops.",
    "DON'T: Default Toronto / Montreal / Boston / JFK / Miami Intl just because they are famous when an executive field nearer the current bases is on the list and legal.",
    "DON'T: Keep filing Atlantic Canada / Port Hawkesbury demand after the company has moved, unless a current base is still in that region.",
    "DON'T: Hard-ban hubs or skip the RFQ rather than file the hub backup. Mx positioning to an MRO on the hub, airline-miss stories, and a hub when no exec field is usable are all legal.",
    "DON'T: Invent ICAOs. Destinations must stay on the Destinations allow-list. Installed / Content.xml fields on that list are fair game when they fit the story.",
    "DON'T: Remint a just-declined or just-flown origin→dest (or that dest) on the next Fetch. Honor mint-memory / cool-down. Familiar-dest memory follows the current bases — a former home that is not on the live base list is not familiar.",
  ].join("\n");
}

export const GROK_INBOX_AIRPORT_BRIEF = formatGrokInboxAirportBrief();
